// デモのWBSデータ（wbs-items.ts の REQUIREMENTS_PHASE_ITEMS）は日付を
// 「day 0（2026-04-01、水曜日）からの経過日数」で持つ。開くたびに常に
// 直近の日付でデモが見えるよう、day 0 を今日を基準にマッピングする。
// 曜日はday 0と同じ水曜日に揃える（曜日に紐づくデータ、例えば火曜開催の
// 会議が週末にずれず、元の曜日パターンのまま表示されるようにするため）。
const MS_PER_HOUR = 60 * 60 * 1000
const MS_PER_DAY = 24 * MS_PER_HOUR
const DAYS_PER_WEEK = 7
const DAY_ZERO_WEEKDAY = 3 // 水曜日（0 = 日曜日）

// 「今日」は要件定義（day 0〜93）の途中（6週目＝42日目、進捗約54%）に置く。
// 過ぎた分は完了・これからの分は未着手として見えるため、完了・対応中・
// レビュー中・未着手がバランス良く散る。要件定義が今まさに進行中の工程で、
// 後続工程はこれから約1年半かけて進む、という計画の見え方になる。
// day 0の曜日を保つ必要があるので、日数ではなく週数で指定する。
const TODAY_WEEK_OFFSET = 6

export const TODAY_DAY_OFFSET = TODAY_WEEK_OFFSET * DAYS_PER_WEEK

const demoScheduleRootMs = (() => {
  const today = new Date()
  const todayUtc = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  )
  const weekdayDiff = (todayUtc.getUTCDay() - DAY_ZERO_WEEKDAY + 7) % 7
  const dayZeroWeekdayMs = todayUtc.getTime() - weekdayDiff * MS_PER_DAY
  return dayZeroWeekdayMs - TODAY_DAY_OFFSET * MS_PER_DAY
})()

export const dayOffsetToDate = (dayOffset: number) => {
  const date = new Date(demoScheduleRootMs + dayOffset * MS_PER_DAY)
  return date.toISOString().slice(0, 10)
}

// デモ日付軸上の日時をepochミリ秒で返す。createdAt等、日付文字列ではなく
// エポック値を持つフィールド向け。
export const dayOffsetToEpochMs = (dayOffset: number, hourOfDay: number = 9) =>
  demoScheduleRootMs + dayOffset * MS_PER_DAY + hourOfDay * MS_PER_HOUR

// 実際の現在日付からの相対日付（YYYY-MM-DD）。
// dayOffsetToDate はday 0の曜日（水曜）に揃えたデモ日付軸上の日付を返すため、
// 今日ちょうどとは最大6日ずれる。「今日から見て前後n日」を素直に表したい
// 用途（検索条件の既定値など）はこちらを使う。
export const todayOffsetToDate = (dayOffset: number) => {
  const today = new Date()
  const todayUtcMs = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  )
  return new Date(todayUtcMs + dayOffset * MS_PER_DAY)
    .toISOString()
    .slice(0, 10)
}

// デモ用データにアイコンを直接持たせるため、SVGをdata URIとして埋め込む。
export const svgDataUri = (svg: string): string =>
  `data:image/svg+xml,${encodeURIComponent(svg)}`

export const applyDelta = <T>(
  currentValue: T,
  delta?: { oldValue?: T; newValue?: T }
): T => (delta ? (delta.newValue as T) : currentValue)

// WBSアイテムの更新系ハンドラ（plans/delta/batch, wbs_items/delta/:type,
// wbs_items/delta/batch）で共通して呼び、登録回数・更新者・更新日時を
// 実アプリと同じ見た目で反映させるためのヘルパー。
export const applyAuditFieldsOnUpdate = <
  T extends { revision?: string; updatedBy?: unknown; updatedAt?: unknown },
>(
  currentBody: T,
  updatedBy: unknown
): void => {
  const currentRevision = Number(currentBody.revision)
  currentBody.revision = String(
    (Number.isNaN(currentRevision) ? 0 : currentRevision) + 1
  )
  currentBody.updatedBy = updatedBy
  currentBody.updatedAt = new Date().getTime()
}

export const FUNCTIONAL_API_URL = 'http://localhost:8091'
export const PRESENTATIONAL_API_URL = 'http://localhost:8093'
